import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'react-table/react-table.css';
import { UncontrolledTooltip } from 'reactstrap';
import { isInteger } from 'lodash-es';

import AuctionGroupActions from './AuctionGroupActions/AuctionGroupActions';
import { isSothebys } from '../../utilities/BusinessLogic';

/**
 * (dyouberg) This class represents the columns which are passed into the ReactTable
 * component.  ReactTable turns this into a beautiful grid.
 *
 * Each element in this array represents the column and what the content of each column
 * should be.  This is reasonably straightforward except for the final column - Actions -
 * which was refactored into its own class.
 *
 * Note that the "values" passed into the cells are the catalog array properties from the parent
 * class.
 */
const AuctionGroupColumns = (groupID, handleDeleteSession, houseID, houseRef, live, type) => [
  {
    Header: 'Cover Photo',
    width: 120,
    accessor: 'imageURL',
    sortable: false,
    Cell: ({ value }) => {
      if (!value) {
        return null;
      }

      return (
        <img
          src={value}
          alt="Cover"
          width="90px"
          height="40px"
          className="auction-cover-photo m-2"
        />
      );
    }
  },
  {
    Header: 'Date & Time',
    width: 180,
    className: 'wrap-table-cell',
    id: 'time',
    accessor: d => d,
    Cell: ({ value: { dateTimeDisplay } }) => {
      if (!dateTimeDisplay) {
        return null;
      }
      return <span>{dateTimeDisplay}</span>;
    }
  },
  {
    Header: 'Session Title',
    id: 'title',
    className: 'wrap-table-cell',
    accessor: d => d.title
  },
  {
    Header: '# of Lots',
    width: 100,
    id: 'counts',
    accessor: ({ lotCount, lotsWithoutImagesCount, unpublishedLotCount, catalogID }) => ({
      lotCount: parseInt(lotCount, 10),
      lotsWithoutImagesCount,
      unpublishedLotCount,
      catalogID
    }),
    sortMethod: (a, b) => (a.lotCount > b.lotCount ? 1 : -1),
    Cell: ({ value: { catalogID, lotCount, unpublishedLotCount, lotsWithoutImagesCount } }) => {
      if (
        !isInteger(catalogID)
        || !isInteger(lotCount)
        || !isInteger(unpublishedLotCount)
        || !isInteger(lotsWithoutImagesCount)
      ) {
        return null;
      }

      const warningTooltipText = `You have ${unpublishedLotCount} ${
        unpublishedLotCount === 1 ? 'unpublished lot' : 'unpublished lots'
      } in this session. Click "Edit Session" to publish your lots.`;
      const flagTooltipText = `You have ${lotsWithoutImagesCount} 
        ${
  lotsWithoutImagesCount === '1' ? 'lot' : 'lots'
} without photos in this session. Click "Edit Session" to add photos.`;

      return (
        <div>
          {type === 'current' && (
            <div className="align-right">
              <span>
                {lotsWithoutImagesCount > 0 && (
                  <span>
                    <FontAwesomeIcon
                      id={`flagIcon_${catalogID}`}
                      className="color-warning"
                      icon="flag"
                    />
                    <UncontrolledTooltip placement="right" target={`flagIcon_${catalogID}`}>
                      {flagTooltipText}
                    </UncontrolledTooltip>
                  </span>
                )}

                {unpublishedLotCount > 0 && (
                  <span>
                    <FontAwesomeIcon
                      id={`warningIcon_${catalogID}`}
                      className="color-danger ml-2"
                      icon="exclamation-triangle"
                    />
                    <UncontrolledTooltip placement="right" target={`warningIcon_${catalogID}`}>
                      {warningTooltipText}
                    </UncontrolledTooltip>
                  </span>
                )}

                {lotCount > 0 && unpublishedLotCount === 0 && lotsWithoutImagesCount === 0 && (
                  <FontAwesomeIcon
                    id={`successIcon_${catalogID}`}
                    icon="check"
                    className="color-success"
                  />
                )}
              </span>
            </div>
          )}
          <div className="align-right">{lotCount + unpublishedLotCount}</div>
        </div>
      );
    }
  },
  // Waiting for api change
  {
    Header: 'Preview Links',
    id: 'catalogID',
    className: 'wrap-table-cell',
    sortable: false,
    width: 140,
    accessor: d => ({
      channels: d.channels,
      catalogID: d.catalogID,
      catalogRef: d.catalogRef,
      lotCount: d.lotCount
    }),
    Cell: ({ value: { channels, catalogID, catalogRef, lotCount } }) => {
      /**
       * (dyouberg) Ok so this took me a little bit to figure out.  For React Table we pass the
       * data we want in the cell as the accessor.  I wanted to have a unique key based off
       * of catalogRef so I extended that information so I could access it in here.
       */
      if (Object.keys(channels).length > 0) {
        return Object.keys(channels).map(channelName => (
          <div key={`${channelName}-${catalogRef.toUpperCase()}`}>
            <a rel="noopener noreferrer" target="_blank" href={channels[channelName]}>
              {channelName === 'Connect' ? 'Private Label' : channelName}
            </a>
          </div>
        ));
      } if (isSothebys(houseID) && lotCount > 0) {
        return (
          <span>
            <UncontrolledTooltip placement="right" target={`whiteLabelTooltip_${catalogID}`}>
              Lots have been published to your private label site and are not available for online
              bidding on Invaluable or AuctionZip.
            </UncontrolledTooltip>
            Private Label Only{' '}
            <FontAwesomeIcon
              className="color-blue"
              id={`whiteLabelTooltip_${catalogID}`}
              icon="question-circle"
            />
          </span>
        );
      }
      return null;
    }
  },
  AuctionGroupActions(groupID, handleDeleteSession, houseID, houseRef, live, type)
];

export default AuctionGroupColumns;
